import { ReactNode, Suspense } from 'react';

import { Skeleton } from '@iwoca/orion';

import {
  useSuspenseGetProfile,
  useSuspenseUserGroups,
} from '../../../../api/lending/lapiHooks.suspense';
import { CustomErrorBoundary } from '../../../../AppWrapper';
import { useSuspenseSellerDetails } from '../../hooks/useSellerDetails';

export const NameTag = () => {
  return (
    <CustomErrorBoundary
      errorComponent={
        <NameTagSkeleton
          initials={<></>}
          fullName={
            <div className="mt-xs h-[12px] w-[100px] rounded-s bg-secondary-90"></div>
          }
          tradingName={
            <div className="mt-xs h-[12px] w-[80px] rounded-s bg-secondary-90"></div>
          }
          companyName={undefined}
        />
      }
    >
      <Suspense
        fallback={
          <NameTagSkeleton
            initials={<></>}
            fullName={<Skeleton size={100} />}
            tradingName={<Skeleton size={80} />}
            companyName={<Skeleton size={70} />}
          />
        }
      >
        <NameTagInner />
      </Suspense>
    </CustomErrorBoundary>
  );
};

const NameTagInner = () => {
  const { profile } = useSuspenseGetProfile();
  const { isOwner, isStaff } = useSuspenseUserGroups();
  const applicantDetails = useSuspenseSellerDetails();

  let fullName: string = `${applicantDetails?.firstName} ${applicantDetails?.lastName}`;
  let initials: string = applicantDetails?.initials || '';

  if (!(isOwner || isStaff)) {
    // TODO(types): fix in schema / add null checks.
    fullName = profile.full_name!;

    const nameParts: string[] = fullName.split(' ');
    if (nameParts.length > 1) {
      initials = `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`;
    } else if (fullName.length > 0) {
      initials = fullName.charAt(0);
    }
  }

  const companyNameDifferentToTradingName =
    applicantDetails?.companyName !== applicantDetails?.tradingName;

  return (
    <NameTagSkeleton
      initials={initials}
      fullName={fullName}
      tradingName={applicantDetails?.tradingName}
      companyName={
        companyNameDifferentToTradingName
          ? applicantDetails?.companyName
          : undefined
      }
    />
  );
};

const NameTagSkeleton = ({
  initials,
  fullName,
  tradingName,
  companyName,
}: {
  initials: ReactNode;
  fullName: ReactNode;
  tradingName: ReactNode;
  companyName?: ReactNode;
}) => {
  return (
    <>
      <div className="grid min-h-[52px] w-full grid-cols-[40px_1fr] gap-m">
        <div className="flex aspect-square w-full items-center justify-center rounded-2xl bg-secondary-80">
          {initials}
        </div>
        <div>
          <div className="max-w-full text-ellipsis text-xs">{fullName}</div>
          <div className="text-xs font-reg">{tradingName}</div>
          {companyName && <div className="text-xs font-reg">{companyName}</div>}
        </div>
      </div>
    </>
  );
};
